import clsx from 'clsx'
import styles from './style.module.scss'

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { TApi, TWidgetDomains } from '../../../shared/const'
import http from '../../../shared/http'
import { customNumberFormatW } from '../../../shared/helpers'
import { useTranslation } from 'react-i18next'
import { widgetHints, widgetLinks } from '../../../shared/widgetListLink'

/*
 * Die Vier-Zahlen-Kachel der „Allgemeinen Information".
 *
 * Wie die Drei-Zahlen-Kachel daneben führt seit 26.08.2026 **jede Zahl** in
 * genau die Zeilen, die sie zählt (shared/widgetListLink). Wo es dafür keine
 * Liste gibt — bei den Finanzzahlen der Berichte, und bei „Registriert" der
 * Schüler:innen, das beim Server eine Restgröße ist — bleibt die Zelle eine
 * Zelle. Ein Verweis auf eine andere Menge wäre schlimmer als keiner.
 */
export default function FourSectionsDataWidget(props) {
	const { t } = useTranslation()
	const [widgetDynamicData, setWidgetDynamicData] = useState({})

	const MAP_DOMAIN_TO_DATA = {
		[TWidgetDomains.COMPANIES]: {
			api: TApi.ADMIN_WIDGET_GET_COMPANIES,
			header: t('companies'),
			leftSideTextUpper: t('Total'),
			leftSideText: t('Pending'),
			rightSideTextUpper: t('Registered'),
			rightSideText: t('Active'),
			activeColor: '#3BB055',
			pendingColor: '#EF8400',
			totalColor: '#303E65',
			registeredColor: '#303E65'
		},
		[TWidgetDomains.STUDENTS]: {
			api: TApi.ADMIN_WIDGET_GET_STUDENTS,
			header: t('students'),
			leftSideTextUpper: t('Total'),
			leftSideText: t('Pending'),
			rightSideTextUpper: t('Registered'),
			rightSideText: t('Active'),
			activeColor: '#3BB055',
			pendingColor: '#EF8400',
			totalColor: '#303E65',
			registeredColor: '#303E65'
		},
		[TWidgetDomains.REPORTS]: {
			api: TApi.ADMIN_WIDGET_GET_REPORTS,
			header: t('reports'),
			leftSideTextUpper: t('Total'),
			leftSideText: t('Pending'),
			rightSideTextUpper: t('Registered'),
			rightSideText: t('Active'),
			activeColor: '#3BB055',
			pendingColor: '#EF8400',
			totalColor: '#303E65',
			registeredColor: '#303E65'
		},
		[TWidgetDomains.INVOICES]: {
			api: TApi.ADMIN_WIDGET_GET_INVOICES,
			header: t('invoices'),
			leftSideTextUpper: t('Total'),
			leftSideText: t('Unpaid'),
			rightSideTextUpper: t('Paid'),
			rightSideText: t('Overdue'),
			// Ohne die Rechnungen im Status "Guthaben" addierten sich die Teilwerte nicht
			// zur Gesamtzahl auf (Anforderung #88).
			extraKey: 'credit',
			extraText: t('Credit'),
			extraColor: '#008B9C',
			// Stornierte Rechnungen stecken nicht in der Gesamtzahl, sondern stehen
			// eigens daneben (Anforderung #91).
			extra2Key: 'cancelled',
			extra2Text: t('Cancelled'),
			extra2Color: '#8B9AA3',
			activeColor: '#EB5757',
			pendingColor: '#EF8400',
			totalColor: '#303E65',
			registeredColor: '#3BB055'
		},
		[TWidgetDomains.TEACHERS]: {
			api: TApi.ADMIN_WIDGET_GET_TEACHERS,
			header: t('teachers'),
			leftSideTextUpper: t('Total'),
			leftSideText: t('Pending'),
			rightSideTextUpper: t('Registered'),
			rightSideText: t('Active'),
			activeColor: '#3BB055',
			pendingColor: '#EF8400',
			totalColor: '#303E65',
			registeredColor: '#303E65'
		},
		[TWidgetDomains.REPORT_DATA]: {
			api: TApi.ADMIN_WIDGET_GET_REPORTS_DATA,
			header: t('reports'),
			leftSideTextUpper: t('Total fin. result'),
			leftSideText: t('Expanses'),
			rightSideTextUpper: t('Revenues'),
			rightSideText: t('Charges'),
			activeColor: '#303E65',
			pendingColor: '#303E65',
			totalColor: '#303E65',
			registeredColor: '#303E65'
		}
	}

	const stateFilter = props.settings?.stateFilter
	const widgetStaticData = MAP_DOMAIN_TO_DATA[props.domain]

	const links = widgetLinks(props.domain, stateFilter)
	const hints = widgetHints(props.domain, t)

	/*
	 * Eine Zelle: Verweis, wenn es ein Ziel gibt — sonst dasselbe als
	 * schmuckloses `div`. Ein Baustein mit oder ohne Ziel, nicht zwei: Sonst
	 * liefen Abstände und Schriftgrade auseinander, sobald jemand nur eine der
	 * beiden Fassungen anfasst.
	 *
	 * `preview` schaltet die Verweise in der Anpassen-Leiste ab — dort wählt
	 * man aus, was auf der Startseite stehen soll.
	 */
	const Zelle = ({ zelle, className, children }) => {
		const ziel = links[zelle]
		const gemeinsam = { className: clsx(className, ziel && styles.verweis), title: hints[zelle] }

		if (!ziel || props.preview) return <div {...gemeinsam}>{children}</div>
		return (
			<Link {...gemeinsam} to={ziel}>
				{children}
			</Link>
		)
	}

	const fetchWidgetData = () => {
		http.get(widgetStaticData.api, {
			params: {
				state: stateFilter
			}
		}).then(({ data }) => {
			setWidgetDynamicData({ ...data.data })
		})
	}

	useEffect(() => {
		fetchWidgetData()
		/* eslint-disable-next-line */
	}, [stateFilter])

	return (
		<div className={styles.root}>
			{/* 8 statt 25 px: Die Kachel ist 180 px hoch (siehe style.module.scss);
			    was hier an Abstand steht, fehlt den drei Zeilen darunter. */}
			<div style={{ marginBottom: '8px' }} className={styles.main}>
				<h1 className={styles.header}>{widgetStaticData.header}</h1>
			</div>
			<div className={styles.firstRow}>
				<Zelle zelle="total" className={styles.firstCol}>
					<h2 style={{ color: widgetStaticData.totalColor }} className={styles.mainNumber}>
						{customNumberFormatW(widgetDynamicData.total)}
					</h2>
					<h4 className={styles.text}>{widgetStaticData.leftSideTextUpper}</h4>
				</Zelle>
				<Zelle zelle="registered" className={styles.secondCol}>
					<h2 style={{ color: widgetStaticData.registeredColor }} className={styles.mainNumber}>
						{customNumberFormatW(widgetDynamicData.registered)}
					</h2>
					<h4 className={styles.text}>{widgetStaticData.rightSideTextUpper}</h4>
				</Zelle>
			</div>
			<div className={styles.secondRow}>
				<Zelle zelle="pending" className={styles.firstCol}>
					<h2 style={{ color: widgetStaticData.pendingColor }} className={styles.mainNumber}>
						{customNumberFormatW(widgetDynamicData.pending)}
					</h2>
					<h4 className={styles.text}>{widgetStaticData.leftSideText}</h4>
				</Zelle>
				<Zelle zelle="active" className={styles.secondCol}>
					<h2 style={{ color: widgetStaticData.activeColor }} className={styles.mainNumber}>
						{customNumberFormatW(widgetDynamicData.active)}
					</h2>
					<h4 className={styles.text}>{widgetStaticData.rightSideText}</h4>
				</Zelle>
			</div>
			{widgetStaticData.extraKey && (
				<div className={styles.secondRow}>
					<Zelle zelle={widgetStaticData.extraKey} className={styles.firstCol}>
						<h2 style={{ color: widgetStaticData.extraColor }} className={styles.mainNumber}>
							{customNumberFormatW(widgetDynamicData[widgetStaticData.extraKey])}
						</h2>
						<h4 className={styles.text}>{widgetStaticData.extraText}</h4>
					</Zelle>
					<Zelle zelle={widgetStaticData.extra2Key} className={styles.secondCol}>
						{widgetStaticData.extra2Key && (
							<>
								<h2 style={{ color: widgetStaticData.extra2Color }} className={styles.mainNumber}>
									{customNumberFormatW(widgetDynamicData[widgetStaticData.extra2Key])}
								</h2>
								<h4 className={styles.text}>{widgetStaticData.extra2Text}</h4>
							</>
						)}
					</Zelle>
				</div>
			)}
		</div>
	)
}
